import { useEffect, useState } from "react";
import { Eye, MapPinned } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatusPill } from "@/components/common";
import { rupiah } from "@/lib/format";

const MemberList = ({ rows, showPetugas, testId }) => (
  rows.length === 0 ? <p className="text-sm text-muted-foreground py-6 text-center" data-testid={`${testId}-empty`}>Belum ada anggota.</p> : (
    <div className="overflow-x-auto" data-testid={testId}>
      <Table>
        <TableHeader><TableRow><TableHead>Anggota</TableHead>{showPetugas && <TableHead>Petugas</TableHead>}<TableHead>Status</TableHead><TableHead className="text-right">Simpanan</TableHead><TableHead className="text-right">Total Pinjaman</TableHead><TableHead className="text-right">Sisa Pinjaman</TableHead></TableRow></TableHeader>
        <TableBody>
          {rows.map((m) => (
            <TableRow key={m.nik}>
              <TableCell><p className="font-medium">{m.name}</p><p className="font-mono text-xs text-muted-foreground">{m.nik}</p></TableCell>
              {showPetugas && <TableCell className="text-sm">{m.petugas_name || "-"}</TableCell>}
              <TableCell><StatusPill active={m.active} /></TableCell>
              <TableCell className="text-right font-mono">{rupiah(m.savings_balance)}</TableCell>
              <TableCell className="text-right font-mono">{rupiah(m.total_pinjaman)}</TableCell>
              <TableCell className="text-right font-mono font-semibold">{rupiah(m.outstanding)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
);

const PetugasMembers = ({ p, onClose }) => {
  const [d, setD] = useState(null);
  useEffect(() => { if (p) api.get(`/reports/petugas/${p.id}/members`).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e))); else setD(null); }, [p]);
  return (
    <Dialog open={Boolean(p)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="petugas-members-dialog">
        <DialogHeader><DialogTitle className="font-heading">Anggota · {p?.name}</DialogTitle></DialogHeader>
        {!d ? <p className="text-sm text-muted-foreground">Memuat...</p> : <MemberList rows={d.members} testId="petugas-members-table" />}
      </DialogContent>
    </Dialog>
  );
};

export const PetugasReport = ({ rows }) => {
  const [sel, setSel] = useState(null);
  return (
    <div className="panel !p-0 overflow-hidden" data-testid="report-petugas">
      <p className="font-heading font-semibold p-6 pb-2">Petugas Lapangan</p>
      {rows.length === 0 ? <p className="text-sm text-muted-foreground px-6 pb-6">Belum ada petugas lapangan.</p> : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead className="pl-6">Petugas</TableHead><TableHead>Resort</TableHead><TableHead className="text-right">Jumlah Pendanaan</TableHead><TableHead className="text-right">Anggota Pinjam</TableHead><TableHead className="text-right">Total Pembiayaan</TableHead><TableHead className="text-right">Sisa Pinjaman</TableHead><TableHead /></TableRow></TableHeader>
            <TableBody>
              {rows.map((p) => (
                <TableRow key={p.id} data-testid={`report-petugas-row-${p.id}`}>
                  <TableCell className="pl-6"><p className="font-medium">{p.name}</p><p className="text-xs text-muted-foreground">{p.anggota_terdaftar} anggota terdaftar</p></TableCell>
                  <TableCell className="text-sm">{p.resort_name || "-"}</TableCell>
                  <TableCell className="text-right font-mono">{rupiah(p.pendanaan)}</TableCell>
                  <TableCell className="text-right font-mono">{p.anggota_pinjam}</TableCell>
                  <TableCell className="text-right font-mono">{rupiah(p.total_pembiayaan)}</TableCell>
                  <TableCell className="text-right font-mono">{rupiah(p.sisa_pinjaman)}</TableCell>
                  <TableCell className="pr-4"><Button size="sm" variant="ghost" className="rounded-full" onClick={() => setSel(p)} data-testid={`report-petugas-detail-${p.id}`}><Eye className="h-4 w-4 mr-1" />Rincian</Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <p className="text-xs text-muted-foreground px-6 pb-5 pt-2">Jumlah pendanaan = total modal/kas bon yang diterima petugas dari kasir.</p>
      <PetugasMembers p={sel} onClose={() => setSel(null)} />
    </div>
  );
};

export const ResortReport = ({ rows }) => (
  <div className="panel" data-testid="report-resort">
    <p className="font-heading font-semibold mb-3">Laporan Resort</p>
    {rows.length === 0 ? <p className="text-sm text-muted-foreground">Belum ada resort.</p> : (
      <Accordion type="multiple" className="space-y-2">
        {rows.map((r) => (
          <AccordionItem key={r.id || "none"} value={r.id || "none"} className="rounded-2xl border px-4" data-testid={`report-resort-${r.id || "none"}`}>
            <AccordionTrigger className="hover:no-underline">
              <div className="flex flex-1 flex-wrap items-center justify-between gap-3 pr-3 text-left">
                <span className="flex items-center gap-2 font-semibold"><MapPinned className="h-4 w-4 text-primary" />{r.name}<span className="text-xs font-normal text-muted-foreground">{r.member_count} anggota</span></span>
                <span className="flex gap-4 text-xs"><span>Pinjaman <b className="font-mono">{rupiah(r.total_pinjaman)}</b></span><span>Sisa <b className="font-mono text-primary">{rupiah(r.sisa_pinjaman)}</b></span></span>
              </div>
            </AccordionTrigger>
            <AccordionContent><MemberList rows={r.members} showPetugas testId={`report-resort-members-${r.id || "none"}`} /></AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    )}
  </div>
);
